import React from 'react';
import { Button } from './button';
import { Linkedin, Facebook, Instagram, Link2, Share2, Check } from 'lucide-react';
import { toast } from 'sonner';

const SocialShareButtons = ({ 
  title = "I'm using Strategic Architect to achieve my goals!",
  description = "AI-powered strategic planning that helps you turn your biggest goals into reality.",
  url = "https://strategicarchitect.net",
  hashtags = ["StrategicArchitect", "GoalSetting", "Strategy", "Productivity"],
  variant = "default" // "default" | "compact" | "icons-only"
}) => {
  const [copied, setCopied] = React.useState(false);
  
  const shareText = `${title}\n\n${description}\n\n${hashtags.map(h => `#${h}`).join(' ')}`;
  
  const shareUrls = {
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(title)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`,
  };
  
  const handleShare = (platform) => {
    const shareUrl = shareUrls[platform];
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };
  
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Strategic Architect',
          text: title,
          url: url,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error('Failed to share');
        }
      }
    } else {
      handleCopyLink();
    }
  };

  if (variant === "icons-only") {
    return (
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleShare('linkedin')}
          className="rounded-full w-9 h-9 p-0 hover:bg-[#0077B5]/10 hover:text-[#0077B5]"
          title="Share on LinkedIn"
        >
          <Linkedin className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleShare('facebook')}
          className="rounded-full w-9 h-9 p-0 hover:bg-[#1877F2]/10 hover:text-[#1877F2]"
          title="Share on Facebook"
        >
          <Facebook className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopyLink}
          className="rounded-full w-9 h-9 p-0 hover:bg-slate-100 dark:hover:bg-slate-800"
          title="Copy link"
        >
          {copied ? <Check className="w-4 h-4 text-green-500" /> : <Link2 className="w-4 h-4" />}
        </Button>
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleNativeShare}
          className="gap-2"
        >
          <Share2 className="w-4 h-4" />
          Share
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">Share Strategic Architect</h3>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleShare('linkedin')}
          className="gap-2 hover:bg-[#0077B5]/10 hover:text-[#0077B5] hover:border-[#0077B5]/30"
        >
          <Linkedin className="w-4 h-4" />
          LinkedIn
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleShare('facebook')}
          className="gap-2 hover:bg-[#1877F2]/10 hover:text-[#1877F2] hover:border-[#1877F2]/30"
        >
          <Facebook className="w-4 h-4" />
          Facebook
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCopyLink}
          className="gap-2"
        >
          {copied ? <Check className="w-4 h-4 text-green-500" /> : <Link2 className="w-4 h-4" />}
          {copied ? 'Copied!' : 'Copy Link'}
        </Button>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Share your strategic journey with your network
      </p>
    </div>
  );
};

export default SocialShareButtons;
